import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Searchable ATECO combobox for the ~3200 codes of ATECO 2025.
 * Renders only the filtered matches (max 200) to stay fast.
 */
export function AtecoCombobox({ items = [], value = "", onChange, placeholder = "Cerca codice o descrizione…" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = useMemo(() => items.find((i) => i.codice === value), [items, value]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) {
      // when no search, show only the first 100 items to avoid rendering 3k+ rows
      return items.slice(0, 100);
    }
    const out = [];
    for (const it of items) {
      const c = (it.codice || "").toLowerCase();
      const d = (it.descrizione || "").toLowerCase();
      if (c.startsWith(s) || c.includes(s) || d.includes(s)) {
        out.push(it);
        if (out.length >= 200) break;
      }
    }
    return out;
  }, [items, search]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between mt-1.5 font-normal"
          data-testid="ateco-combobox-trigger"
        >
          <span className="truncate text-left">
            {selected ? (
              <>
                <span className="font-mono font-semibold">{selected.codice}</span>
                <span className="text-slate-500"> — {selected.descrizione}</span>
              </>
            ) : (
              <span className="text-slate-400">Seleziona codice ATECO…</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(600px,95vw)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={setSearch}
            data-testid="ateco-combobox-search"
          />
          <CommandList>
            <CommandEmpty>Nessun codice trovato.</CommandEmpty>
            <CommandGroup>
              {filtered.map((it) => (
                <CommandItem
                  key={it.codice}
                  value={it.codice}
                  onSelect={() => {
                    onChange?.(it.codice, it);
                    setOpen(false);
                  }}
                  className="flex items-start gap-2"
                  data-testid={`ateco-option-${it.codice}`}
                >
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      value === it.codice ? "opacity-100 text-[#0F2C59]" : "opacity-0"
                    )}
                  />
                  <span className="font-mono text-xs font-semibold text-[#0F2C59] w-20 shrink-0">
                    {it.codice}
                  </span>
                  <span className="text-sm text-slate-700 flex-1">{it.descrizione}</span>
                  <span
                    className={cn(
                      "text-[10px] uppercase font-mono px-1.5 py-0.5 rounded border",
                      it.rischio === "alto" && "bg-red-50 text-red-700 border-red-300",
                      it.rischio === "medio" && "bg-orange-50 text-orange-700 border-orange-300",
                      it.rischio === "basso" && "bg-green-50 text-green-700 border-green-300"
                    )}
                  >
                    {it.rischio}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            {!search && items.length > filtered.length && (
              <div className="px-3 py-2 text-xs text-slate-400 border-t">
                Mostrati {filtered.length} di {items.length}. Digita per cercare.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
